import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccountService } from 'src/app/services/account.service';

@Component({
  selector: 'app-default-profile-dialog',
  templateUrl: './default-profile-dialog.component.html',
  styleUrls: ['./default-profile-dialog.component.css']
})
export class DefaultProfileDialogComponent implements OnInit {
  accountId: number;
  paymentProfiles: any[] = [];
  selectedProfileId: string;
  selectedJaldeeBank = true;
  api_success: string;
  api_error: any;
  disableConfirmbtn = false;

  constructor(
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private accountService: AccountService
  ) {}

  ngOnInit() {
    this.accountId = this.data.accountId;
    this.loadInitialData();
  }

  loadInitialData() {
    this.accountService.getPayBankDetails(this.accountId)
      .subscribe((payBankDetails: any) => {
        this.setInitialSelection(payBankDetails || {});
        this.loadPaymentProfiles();
      }, () => {
        this.setInitialSelection({});
        this.loadPaymentProfiles();
      });
  }

  loadPaymentProfiles() {
    this.accountService.getPaymentProfiles(this.accountId)
      .subscribe((profiles: any) => {
        this.paymentProfiles = Array.isArray(profiles) ? profiles : [];
        this.syncSelectedProfileWithBank();
      }, (error) => {
        this.api_error = error.error;
      });
  }

  setInitialSelection(payBankDetails: any) {
    const hasSavedSelection = !!payBankDetails.defaultPaymentProfileId;
    this.selectedJaldeeBank = hasSavedSelection && typeof payBankDetails.jaldeeBank === 'boolean' ? payBankDetails.jaldeeBank : true;
    this.selectedProfileId = payBankDetails.defaultPaymentProfileId || '';
  }

  selectProfile(profile: any) {
    if (!this.canSelectProfile(profile)) {
      return;
    }
    this.selectedProfileId = this.getProfileId(profile);
  }

  onBankSelectionChange() {
    this.syncSelectedProfileWithBank(true);
  }

  canSelectProfile(profile: any): boolean {
    return this.isProfileForSelectedBank(profile);
  }

  updateDefaultProfile() {
    if (!this.selectedProfileId) {
      this.api_error = 'Select a payment profile';
      return;
    }
    this.api_error = '';
    this.api_success = '';
    this.disableConfirmbtn = true;

    const payload = {
      jaldeeBank: this.selectedJaldeeBank,
      defaultPaymentProfileId: this.selectedProfileId
    };

    this.accountService.updateDefaultPayBank(this.accountId, payload)
      .subscribe(() => {
        this.accountService.getPayBankDetails(this.accountId)
          .subscribe(() => {
            this.api_success = 'Updated successfully';
            setTimeout(() => {
              this.dialogRef.close('reloadlist');
            }, 1500);
          }, (error) => {
            this.api_error = error.error;
            this.disableConfirmbtn = false;
          });
      }, (error) => {
        this.api_error = error.error;
        this.disableConfirmbtn = false;
      });
  }

  getProfileId(profile: any): string {
    return profile.defaultPaymentProfileId || profile.paymentProfileId || profile.profileId || profile.id || profile.bankId || '';
  }

  getProfileLabel(profile: any): string {
    return profile.displayName || profile.profileName || profile.bankName || profile.accountHolderName || this.getProfileId(profile);
  }

  getJaldeeBankValue(profile: any): boolean {
    return !!(profile.jaldeeBank ?? profile.isJaldeeBank);
  }

  private syncSelectedProfileWithBank(forceSelection = false) {
    if (!this.paymentProfiles.length) {
      this.selectedProfileId = '';
      return;
    }

    const selectedProfile = this.paymentProfiles.find(profile => this.getProfileId(profile) === this.selectedProfileId);
    const selectedMatchesBank = selectedProfile && this.isProfileForSelectedBank(selectedProfile);
    if (!forceSelection && selectedMatchesBank) {
      return;
    }

    const matchingProfile = this.getPreferredProfileForSelectedBank()
      || this.paymentProfiles.find(profile => this.canSelectProfile(profile));
    this.selectedProfileId = this.getProfileId(matchingProfile || this.paymentProfiles[0]);
  }

  private getPreferredProfileForSelectedBank() {
    const preferredNames = this.selectedJaldeeBank
      ? ['JALDEE BANK PAYMENT PROFILE', 'JALDEE BANK PROFILE']
      : ['WELLNESS PAYMENT PROFILE', 'WELL NESS BANK PROFILE'];
    return this.paymentProfiles.find(profile => preferredNames.includes(this.getProfileLabel(profile).toUpperCase()));
  }

  private isProfileForSelectedBank(profile: any): boolean {
    return this.isJaldeeBankProfile(profile) === this.selectedJaldeeBank;
  }

  private isJaldeeBankProfile(profile: any): boolean {
    const profileName = this.getProfileLabel(profile).toUpperCase();
    if (profileName === 'JALDEE BANK PAYMENT PROFILE' || profileName === 'JALDEE BANK PROFILE') {
      return true;
    }
    if (profileName === 'WELLNESS PAYMENT PROFILE' || profileName === 'WELL NESS BANK PROFILE') {
      return false;
    }
    return this.getJaldeeBankValue(profile);
  }
}
